import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import FlyCodeBanner from "../components/home/FlyCodeBanner";
import FooterSection from "../components/home/FooterSection";
import BlueGradientButton from "../components/BlueGradientButton";
import blueAppIcon from "../images/blueapp10.png";
import checkIcon from "../images/check.png";
import xIcon from "../images/x.svg";

// Import CSS files
import "../styles/home/scrollbar.css";
import "../styles/home/layout.css";
import "../styles/home/components.css";

const Pricing = () => {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const [isAnnual, setIsAnnual] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [animatedPrice, setAnimatedPrice] = useState("$25");
  const [periodText, setPeriodText] = useState("/month");
  const [priceOpacity, setPriceOpacity] = useState(1);
  const [priceTransform, setPriceTransform] = useState("translateX(0px)");
  const [periodOpacity, setPeriodOpacity] = useState(1);
  const [periodTransform, setPeriodTransform] = useState("translateY(0px)");
  const [isAnimating, setIsAnimating] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState("free"); // User's current payment tier

  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    setIsMobile(mobile);
  }, [mobile]);

  // Fetch user's payment status to determine if they already have an active plan
  useEffect(() => {
    if (user) {
      const fetchStatus = async () => {
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            setPaymentStatus(userDoc.data().paymentStatus || "free");
          }
        } catch (error) {
          console.error("[Pricing] Error fetching payment status:", error);
        }
      };
      fetchStatus();
    }
  }, [user]);

  // Handle Subscribe button click - check auth first
  const handleSubscribeClick = () => {
    if (!user) {
      // User not logged in, redirect to login
      navigate("/login");
    } else {
      // User is logged in, proceed to Stripe with prefilled email
      const stripeUrl = isAnnual
        ? "https://buy.stripe.com/28E14m6jCe700hM3289IQ01"
        : "https://buy.stripe.com/9B600i6jC0ga8OidGM9IQ00";

      // Add customer email as URL parameter so Stripe knows who paid
      const stripeUrlWithEmail = `${stripeUrl}?prefilled_email=${encodeURIComponent(user.email)}&client_reference_id=${user.uid}`;
      window.location.href = stripeUrlWithEmail;
    }
  };

  // Manual animation using React state
  useEffect(() => {
    console.log("Animation triggered, isAnnual:", isAnnual);

    setIsAnimating(true);

    // Fade out current values first
    setPriceOpacity(0);
    setPriceTransform("translateY(10px)");
    setPeriodOpacity(0);
    setPeriodTransform("translateY(6px)");

    // Use requestAnimationFrame to ensure smooth transition
    requestAnimationFrame(() => {
      // Update values immediately after fade out starts
      requestAnimationFrame(() => {
        setAnimatedPrice(isAnnual ? "$250" : "$25");
        setPeriodText(isAnnual ? "/year" : "/month");

        // Animate both price and period together with synchronized timing
        const startTime = Date.now();
        const duration = 400; // Faster animation

        const animate = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);

          // Smooth easing function (ease-out-cubic)
          const easeOut = 1 - Math.pow(1 - progress, 3);

          // Animate price
          setPriceOpacity(easeOut);
          setPriceTransform(`translateY(${10 * (1 - easeOut)}px)`);

          // Animate period with slight delay for staggered effect
          const periodProgress = Math.min((elapsed - 50) / duration, 1);
          const periodEaseOut =
            periodProgress > 0 ? 1 - Math.pow(1 - periodProgress, 3) : 0;
          setPeriodOpacity(periodEaseOut);
          setPeriodTransform(`translateY(${6 * (1 - periodEaseOut)}px)`);

          if (progress < 1) {
            requestAnimationFrame(animate);
          } else {
            // Ensure final state
            setPriceOpacity(1);
            setPriceTransform("translateY(0px)");
            setPeriodOpacity(1);
            setPeriodTransform("translateY(0px)");
            setIsAnimating(false);
          }
        };

        requestAnimationFrame(animate);
      });
    });
  }, [isAnnual]);

  // Pricing Plans Data - Cluely style (Pro and Enterprise only)
  const pricingPlans = [
    {
      id: 1,
      name: "Pro",
      price: isAnnual ? "$250" : "$25",
      monthlyPrice: "$25",
      annualPrice: "$250",
      period: isAnnual ? "/year" : "/month",
      button: {
        /* Show "Current Plan" for paid/org users instead of Subscribe */
        text: (paymentStatus === "paid" || paymentStatus === "org") ? "Current Plan" : "Subscribe",
        variant: "contained",
        disabled: paymentStatus === "paid" || paymentStatus === "org",
      },
      features: [
        "Full access to our suite of tools",
        "Priority support",
        "Community forums coming soon",
      ],
      popular: true,
    },
    {
      id: 2,
      name: "Enterprise",
      price: "Custom",
      monthlyPrice: "Custom",
      annualPrice: "Custom",
      button: {
        text: "Let's talk",
        href: "/contact-form",
        variant: "outlined",
      },
      features: [
        "Everything in Pro, plus...",
        "Access to new features and tools",
        "Discounted rates",
        "Customized onboarding",
        "Choose a cause, we donate",
        "And more",
      ],
      popular: false,
    },
  ];

  // Feature comparison data organized by sections
  const featuresSections = {
    features: [
      {
        name: "Full access to study suite",
        pro: true,
        enterprise: true,
      },
      {
        name: "Early access to new features and tools",
        pro: false,
        enterprise: true,
      },
      {
        name: "Choose a cause, we donate",
        pro: false,
        enterprise: true,
      },
      {
        name: "Discounted rates",
        pro: false,
        enterprise: true,
      },
    ],
    platform: [
      {
        name: "Advanced analytics",
        pro: false,
        enterprise: "Coming soon",
      },
      {
        name: "Centralized billing",
        pro: false,
        enterprise: true,
      },
    ],
    support: [
      {
        name: "Community forums",
        pro: "Coming soon",
        enterprise: "Coming soon",
      },
      {
        name: "Priority support",
        pro: true,
        enterprise: true,
      },
      {
        name: "Customized onboarding",
        pro: false,
        enterprise: true,
      },
    ],
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 40%)",
        color: "#000000",
        paddingTop: { xs: "80px", md: "100px" },
      }}
    >
      <FlyCodeBanner />
      <Navbar />

      {/* Hero Section */}
      <Box
        sx={{
          paddingY: { xs: "60px", md: "80px" },
          paddingX: { xs: "20px", md: "80px" },
          textAlign: "center",
          maxWidth: "1600px",
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            marginBottom: "16px",
          }}
        >
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "3rem", md: "4rem" },
              fontWeight: 600,
              color: "#000000",
            }}
          >
            Start
          </Typography>
          <img
            src={blueAppIcon}
            alt="EduTools Logo"
            style={{
              width: "100px",
              height: "100px",
              transform: "rotate(-3deg)",
              filter:
                "drop-shadow(4px 6px 12px rgba(59, 130, 246, 0.4)) drop-shadow(0 0 4px rgba(59, 130, 246, 0.2))",
            }}
          />
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "3rem", md: "4rem" },
              fontWeight: 600,
              color: "#000000",
            }}
          >
            today.
          </Typography>
        </Box>
        <Typography
          variant="body1"
          sx={{
            fontSize: { xs: "1.1rem", md: "1.25rem" },
            color: "#666666",
            marginBottom: "40px",
            maxWidth: "600px",
            margin: "0 auto 40px auto",
          }}
        >
          One platform, all the tools you need to succeed as a student.
        </Typography>

        {/* Billing Toggle - Exact Cluely Clone */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "16px",
            marginBottom: "40px",
          }}
        >
          <Typography
            onClick={() => {
              console.log("Monthly clicked");
              setIsAnnual(false);
            }}
            sx={{
              color: !isAnnual ? "#000000" : "#666666",
              fontWeight: 500,
              fontSize: "16px",
              cursor: "pointer",
              userSelect: "none",
              minWidth: "60px",
              textAlign: "center",
              transition: "color 0.3s ease",
            }}
          >
            Monthly
          </Typography>

          <Box
            sx={{
              position: "relative",
              width: "50px",
              height: "28px",
              backgroundColor: "#ffffff",
              border: "1px solid #e0e0e0",
              borderRadius: "14px",
              boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
              cursor: "pointer",
            }}
            onClick={() => {
              console.log("Toggle clicked, isAnnual:", isAnnual);
              setIsAnnual(!isAnnual);
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: "2px",
                left: isAnnual ? "24px" : "2px",
                width: "22px",
                height: "22px",
                backgroundColor: "#333333",
                borderRadius: "50%",
                boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                transition:
                  "left 0.8s cubic-bezier(0.23, 1, 0.32, 1) !important",
                WebkitTransition:
                  "left 0.8s cubic-bezier(0.23, 1, 0.32, 1) !important",
                MozTransition:
                  "left 0.8s cubic-bezier(0.23, 1, 0.32, 1) !important",
                OTransition:
                  "left 0.8s cubic-bezier(0.23, 1, 0.32, 1) !important",
                msTransition:
                  "left 0.8s cubic-bezier(0.23, 1, 0.32, 1) !important",
              }}
            />
          </Box>

          <Typography
            onClick={() => {
              console.log("Annually clicked");
              setIsAnnual(true);
            }}
            sx={{
              color: isAnnual ? "#000000" : "#666666",
              fontWeight: 500,
              fontSize: "16px",
              cursor: "pointer",
              userSelect: "none",
              minWidth: "60px",
              textAlign: "center",
              transition: "color 0.3s ease",
            }}
          >
            Annually
          </Typography>
        </Box>
      </Box>

      {/* Pricing Plans */}
      <Box
        sx={{
          paddingX: { xs: "20px", md: "80px" },
          marginBottom: "120px",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
            gap: "32px",
            maxWidth: "600px",
            margin: "0 auto",
          }}
        >
          {pricingPlans.map((plan) => (
            <Card
              key={plan.id}
              sx={{
                position: "relative",
                border: "none",
                borderRadius: "12px",
                padding: "24px",
                textAlign: "left",
                backgroundColor:
                  plan.name === "Enterprise" ? "#f1f5f9" : "#fafbfc",
                boxShadow:
                  plan.name === "Enterprise"
                    ? "8px 8px 24px rgba(0,0,0,0.12), inset 0 2px 4px rgba(255,255,255,0.8), inset 0 -1px 2px rgba(0,0,0,0.1), 0 0 0 1px rgba(255,255,255,0.8), inset 0 0 20px rgba(255,255,255,1.0), inset 0 0 40px rgba(255,255,255,0.9), inset 0 0 60px rgba(248,250,255,0.8), inset 0 0 80px rgba(240,248,255,0.6)"
                    : "8px 8px 24px rgba(0,0,0,0.12), inset 0 1px 2px rgba(255,255,255,0.6), inset 0 -1px 1px rgba(0,0,0,0.05), 0 0 0 1px rgba(255,255,255,0.8), inset 0 0 20px rgba(255,255,255,0.2)",
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontSize: "1.25rem",
                  fontWeight: 550,
                  marginBottom: "16px",
                  color: "#000000",
                }}
              >
                {plan.name}
              </Typography>

              <Typography
                variant="h2"
                sx={{
                  fontSize: "2rem",
                  fontWeight: 550,
                  marginBottom: "16px",
                  color: "#000000",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {plan.name === "Pro" ? (
                  <Box
                    component="span"
                    sx={{
                      display: "inline-block",
                      opacity: priceOpacity,
                      transform: priceTransform,
                    }}
                  >
                    {animatedPrice}
                  </Box>
                ) : (
                  plan.price
                )}
                {plan.period && (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 400,
                      color: "#666666",
                      display: "inline-block",
                      opacity: periodOpacity,
                      transform: periodTransform,
                      marginLeft: "4px",
                    }}
                  >
                    {plan.name === "Pro" ? periodText : plan.period}
                  </Typography>
                )}
              </Typography>

              <Button
                variant={plan.button.variant}
                disabled={plan.button.disabled}
                onClick={
                  plan.button.disabled
                    ? undefined
                    : plan.name === "Pro"
                      ? handleSubscribeClick
                      : () => (window.location.href = plan.button.href)
                }
                sx={{
                  width: "100%",
                  py: 1,
                  mb: 3,
                  borderRadius: "6px",
                  textTransform: "none",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  backgroundColor: plan.button.disabled
                    ? "#e5e7eb"
                    : plan.name === "Pro" ? "#333333" : "#3b82f6",
                  color: plan.button.disabled
                    ? "#6b7280"
                    : "#ffffff",
                  cursor: plan.button.disabled ? "default" : "pointer",
                  "&:hover": {
                    backgroundColor: plan.button.disabled
                      ? "#e5e7eb"
                      : plan.name === "Pro" ? "#1f2937" : "#2563eb",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#e5e7eb",
                    color: "#6b7280",
                  },
                }}
              >
                {plan.button.text}
              </Button>

              {/* Divider */}
              <Box
                sx={{
                  height: "1px",
                  backgroundColor: "#e0e0e0",
                  marginBottom: "20px",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                }}
              />

              {plan.features.map((feature, index) => (
                <Typography
                  key={index}
                  variant="body2"
                  sx={{
                    marginBottom: "8px",
                    color: "#333333",
                    fontSize: "0.875rem",
                  }}
                >
                  {feature}
                </Typography>
              ))}
            </Card>
          ))}
        </Box>
      </Box>

      {/* Feature Comparison Table - Desktop Only */}
      {!isMobile && (
        <Box
          id="feature-comparison"
          sx={{
            paddingX: { xs: "20px", md: "80px" },
            marginBottom: "-301px",
            position: "relative",
            height: "100vh",
            overflow: "hidden",
          }}
        >
          {/* Desktop Sticky Table */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <TableContainer
              sx={{
                backgroundColor: "transparent",
                maxWidth: "1100px",
                width: "100%",
                position: "relative",
                height: "calc(100vh + 200px)",
                overflow: "hidden",
              }}
            >
              <Table>
                <TableHead sx={{ borderBottom: "none", paddingTop: "10px" }}>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: "1.1rem",
                        borderBottom: "none",
                      }}
                    ></TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        textAlign: "left",
                        fontSize: "1.1rem",
                        borderBottom: "none",
                        paddingTop: "40px",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 600, marginBottom: "4px" }}
                        >
                          Pro
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "#666666", marginBottom: "8px" }}
                        >
                          {isAnnual ? "$250/year" : "$25/month"}
                        </Typography>
                        <Button
                          variant="contained"
                          disabled={paymentStatus === "paid" || paymentStatus === "org"}
                          onClick={
                            (paymentStatus === "paid" || paymentStatus === "org")
                              ? undefined
                              : handleSubscribeClick
                          }
                          sx={{
                            py: 0.5,
                            px: 2,
                            borderRadius: "6px",
                            textTransform: "none",
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            backgroundColor: (paymentStatus === "paid" || paymentStatus === "org") ? "#e5e7eb" : "#333333",
                            color: (paymentStatus === "paid" || paymentStatus === "org") ? "#6b7280" : "#ffffff",
                            minWidth: "200px",
                            cursor: (paymentStatus === "paid" || paymentStatus === "org") ? "default" : "pointer",
                            "&:hover": {
                              backgroundColor: (paymentStatus === "paid" || paymentStatus === "org") ? "#e5e7eb" : "#1f2937",
                            },
                            "&.Mui-disabled": {
                              backgroundColor: "#e5e7eb",
                              color: "#6b7280",
                            },
                          }}
                        >
                          {(paymentStatus === "paid" || paymentStatus === "org") ? "Current Plan" : "Subscribe"}
                        </Button>
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        textAlign: "left",
                        fontSize: "1.1rem",
                        borderBottom: "none",
                        paddingTop: "40px",
                        position: "relative",
                        zIndex: 10,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, marginBottom: "4px" }}
                      >
                        Enterprise
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#666666", marginBottom: "8px" }}
                      >
                        Custom
                      </Typography>
                      <Button
                        variant="contained"
                        href="/contact-form"
                        sx={{
                          py: 0.5,
                          px: 2,
                          borderRadius: "6px",
                          textTransform: "none",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                          backgroundColor: "#3b82f6",
                          color: "#ffffff",
                          minWidth: "200px",
                          "&:hover": {
                            backgroundColor: "#2563eb",
                          },
                        }}
                      >
                        Let's talk
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Features Section */}
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      sx={{
                        fontWeight: 600,
                        fontSize: "1rem",
                        paddingY: 1,
                        paddingTop: 2,
                        textAlign: "left",
                      }}
                    >
                      Features
                    </TableCell>
                  </TableRow>
                  {featuresSections.features.map((feature, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ fontWeight: 500, paddingY: 1 }}>
                        {feature.name}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "left",
                          position: "relative",
                          zIndex: 10,
                          paddingY: 0.5,
                        }}
                      >
                        {typeof feature.pro === "boolean" ? (
                          feature.pro ? (
                            <img
                              src={checkIcon}
                              alt="check"
                              style={{
                                width: "20px",
                                height: "20px",
                                filter:
                                  "brightness(0) saturate(100%) invert(27%) sepia(96%) saturate(1352%) hue-rotate(213deg) brightness(119%) contrast(119%)",
                              }}
                            />
                          ) : (
                            <img
                              src={xIcon}
                              alt="x"
                              style={{
                                width: "20px",
                                height: "20px",
                                filter:
                                  "brightness(0) saturate(100%) invert(60%) sepia(0%) saturate(0%) hue-rotate(93deg) brightness(95%) contrast(86%)",
                              }}
                            />
                          )
                        ) : (
                          feature.pro
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "left",
                          position: "relative",
                          zIndex: 10,
                          paddingY: 0.5,
                        }}
                      >
                        {typeof feature.enterprise === "boolean" ? (
                          feature.enterprise ? (
                            <img
                              src={checkIcon}
                              alt="check"
                              style={{
                                width: "20px",
                                height: "20px",
                                filter:
                                  "brightness(0) saturate(100%) invert(27%) sepia(96%) saturate(1352%) hue-rotate(213deg) brightness(119%) contrast(119%)",
                              }}
                            />
                          ) : (
                            <Typography
                              sx={{
                                color: "#9ca3af",
                                fontSize: "14px",
                                fontWeight: 300,
                              }}
                            >
                              ✗
                            </Typography>
                          )
                        ) : (
                          feature.enterprise
                        )}
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* Platform Section */}
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      sx={{
                        fontWeight: 600,
                        fontSize: "1rem",
                        paddingY: 1,
                        paddingTop: 2,
                        textAlign: "left",
                      }}
                    >
                      Platform
                    </TableCell>
                  </TableRow>
                  {featuresSections.platform.map((feature, index) => (
                    <TableRow key={`platform-${index}`}>
                      <TableCell sx={{ fontWeight: 500, paddingY: 1 }}>
                        {feature.name}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "left",
                          position: "relative",
                          zIndex: 10,
                          paddingY: 0.5,
                        }}
                      >
                        {typeof feature.pro === "boolean" ? (
                          feature.pro ? (
                            <img
                              src={checkIcon}
                              alt="check"
                              style={{
                                width: "20px",
                                height: "20px",
                                filter:
                                  "brightness(0) saturate(100%) invert(27%) sepia(96%) saturate(1352%) hue-rotate(213deg) brightness(119%) contrast(119%)",
                              }}
                            />
                          ) : (
                            <img
                              src={xIcon}
                              alt="x"
                              style={{
                                width: "20px",
                                height: "20px",
                                filter:
                                  "brightness(0) saturate(100%) invert(60%) sepia(0%) saturate(0%) hue-rotate(93deg) brightness(95%) contrast(86%)",
                              }}
                            />
                          )
                        ) : (
                          feature.pro
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "left",
                          position: "relative",
                          zIndex: 10,
                          paddingY: 0.5,
                        }}
                      >
                        {typeof feature.enterprise === "boolean" ? (
                          feature.enterprise ? (
                            <img
                              src={checkIcon}
                              alt="check"
                              style={{
                                width: "20px",
                                height: "20px",
                                filter:
                                  "brightness(0) saturate(100%) invert(27%) sepia(96%) saturate(1352%) hue-rotate(213deg) brightness(119%) contrast(119%)",
                              }}
                            />
                          ) : (
                            <img
                              src={xIcon}
                              alt="x"
                              style={{
                                width: "20px",
                                height: "20px",
                                filter:
                                  "brightness(0) saturate(100%) invert(60%) sepia(0%) saturate(0%) hue-rotate(93deg) brightness(95%) contrast(86%)",
                              }}
                            />
                          )
                        ) : (
                          feature.enterprise
                        )}
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* Support Section */}
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      sx={{
                        fontWeight: 600,
                        fontSize: "1rem",
                        paddingY: 1,
                        paddingTop: 2,
                        textAlign: "left",
                      }}
                    >
                      Support
                    </TableCell>
                  </TableRow>
                  {featuresSections.support.map((feature, index) => (
                    <TableRow key={`support-${index}`}>
                      <TableCell sx={{ fontWeight: 500, paddingY: 1 }}>
                        {feature.name}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "left",
                          position: "relative",
                          zIndex: 10,
                          paddingY: 0.5,
                        }}
                      >
                        {typeof feature.pro === "boolean" ? (
                          feature.pro ? (
                            <img
                              src={checkIcon}
                              alt="check"
                              style={{
                                width: "20px",
                                height: "20px",
                                filter:
                                  "brightness(0) saturate(100%) invert(27%) sepia(96%) saturate(1352%) hue-rotate(213deg) brightness(119%) contrast(119%)",
                              }}
                            />
                          ) : (
                            <img
                              src={xIcon}
                              alt="x"
                              style={{
                                width: "20px",
                                height: "20px",
                                filter:
                                  "brightness(0) saturate(100%) invert(60%) sepia(0%) saturate(0%) hue-rotate(93deg) brightness(95%) contrast(86%)",
                              }}
                            />
                          )
                        ) : (
                          feature.pro
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "left",
                          position: "relative",
                          zIndex: 10,
                          paddingY: 0.5,
                        }}
                      >
                        {typeof feature.enterprise === "boolean" ? (
                          feature.enterprise ? (
                            <img
                              src={checkIcon}
                              alt="check"
                              style={{
                                width: "20px",
                                height: "20px",
                                filter:
                                  "brightness(0) saturate(100%) invert(27%) sepia(96%) saturate(1352%) hue-rotate(213deg) brightness(119%) contrast(119%)",
                              }}
                            />
                          ) : (
                            <img
                              src={xIcon}
                              alt="x"
                              style={{
                                width: "20px",
                                height: "20px",
                                filter:
                                  "brightness(0) saturate(100%) invert(60%) sepia(0%) saturate(0%) hue-rotate(93deg) brightness(95%) contrast(86%)",
                              }}
                            />
                          )
                        ) : (
                          feature.enterprise
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      )}

      {/* Mobile Disclaimer */}
      {isMobile && (
        <Box
          sx={{
            paddingX: "20px",
            marginBottom: "60px",
            textAlign: "center",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#666666",
              fontSize: "14px",
              fontStyle: "italic",
            }}
          >
            *more subscription details on desktop
          </Typography>
        </Box>
      )}

      {/* Original CallToActionSection for comparison */}
      <div
        className="section"
        data-aos="fade-up"
        style={{ backgroundColor: "#ffffff" }}
      >
        <div
          className="container"
          style={{ marginTop: "120px", marginBottom: "120px" }}
        >
          <div className="cta-holder-06">
            <div className="cta-left-right-grid">
              <div className="fade-in-on-scroll">
                <div className="cta-heading-holder ease-larger">
                  <h3 className="second-title" style={{ color: "#ffffff" }}>
                    Built for the modern student
                  </h3>
                  <p
                    style={{
                      color: "#b4bcd0",
                      marginTop: "12px",
                      fontSize: "1.1rem",
                    }}
                  >
                    Start using EduTools today
                  </p>
                </div>
              </div>
              <div className="fade-in-on-scroll">
                <div className="cta-button-holder">
                  <a
                    href="/download"
                    className="button secondary w-inline-block"
                  >
                    <div>Explore</div>
                    <img
                      loading="lazy"
                      src="./images/Arrow-Right.svg"
                      alt="Arrow Right"
                      className="button-arrow-icon"
                    />
                  </a>
                  <BlueGradientButton href="/download">
                    Download now
                  </BlueGradientButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ backgroundColor: "#0f0f0f" }}>
        <FooterSection />
      </div>
    </Box>
  );
};

export default Pricing;
